<template>
  <CardLayout v-bind="clickable ? { onClick: () => $emit('click') } : {}">
    <template #header>
      {{ $t('report.members_title') }}
    </template>
    <div v-if="loading">
      <Loading />
    </div>
    <div v-else-if="membersTotal > 0" class="w-full">
      <div class="grid grid-cols-2 gap-4">
        <!-- Left: new registrations + evolution -->
        <div class="flex flex-col">
          <div class="flex items-center gap-1.5 mb-2">
            <div class="w-1.5 h-1.5 rounded-full" style="background-color: #06C8FF"></div>
            <span class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              {{ $t('report.members_new_registrations') }}
            </span>
          </div>
          <span class="text-[16px] font-bold text-foreground leading-tight">
            {{ membersTotal.toLocaleString() }}
          </span>
          <span v-if="membersEvolution !== null"
            class="text-[11px] font-semibold mt-1"
            :style="{ color: membersEvolution > 0 ? '#16A34A' : membersEvolution < 0 ? '#EF4444' : '#6B7280' }">
            {{ membersEvolution > 0 ? '+' : '' }}{{ membersEvolution.toFixed(1) }}%
          </span>
        </div>
        <!-- Right: registration vector -->
        <div v-if="topConversionFamily" class="flex flex-col">
          <div class="flex items-center gap-1.5 mb-2">
            <div class="w-1.5 h-1.5 rounded-full" :style="{ backgroundColor: colorsUtils.getColorForName(topConversionFamily.name) }"></div>
            <span class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              {{ $t('report.members_conversion_vector') }}
            </span>
          </div>
          <span class="text-[16px] font-bold text-foreground leading-tight">
            {{ topConversionFamily.name }}
          </span>
          <span class="text-[11px] text-muted-foreground mt-1">
            {{ topConversionFamily.pct }}% des inscrits
          </span>
        </div>
      </div>
    </div>
    <div v-else class="w-full flex flex-col items-center justify-center py-6">
      <p class="text-sm text-muted-foreground">{{ $t('data.unavailable') }}</p>
    </div>
  </CardLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { MembersStatsResponse } from '../../responses'
import CardLayout from '../CardLayout.vue'
import Loading from '../Loading.vue'
import { colorsUtils } from '../../utils/ColorsUtils'

const props = withDefaults(defineProps<{
  membersReport: MembersStatsResponse | null | undefined
  membersConversionData: MembersStatsResponse | null | undefined
  loading: boolean
  clickable?: boolean
}>(), {
  clickable: true,
})

defineEmits<{
  click: []
}>()

const membersTotal = computed(() => {
  const report = props.membersReport
  if (!report?.data) return 0
  return report.data.reduce((sum, d) => sum + (d.metrics.count ?? 0), 0)
})

const membersEvolution = computed(() => {
  const report = props.membersReport
  if (!report?.intervals || !report?.previous_intervals) return null
  if (!report.equitable_period || !report.equitable_previous_period) return null

  const eqEnd = new Date(report.equitable_period.end).getTime()
  const eqPrevEnd = new Date(report.equitable_previous_period.end).getTime()

  // Sum current intervals within equitable period
  const currentTotal = report.intervals
    .filter(iv => new Date(iv.date).getTime() <= eqEnd)
    .reduce((sum, iv) => sum + iv.data.reduce((s, d) => s + (d.metrics.count ?? 0), 0), 0)

  // Sum previous intervals within equitable previous period
  const prevTotal = report.previous_intervals
    .filter(iv => new Date(iv.date).getTime() <= eqPrevEnd)
    .reduce((sum, iv) => sum + iv.data.reduce((s, d) => s + (d.metrics.count ?? 0), 0), 0)

  if (prevTotal === 0) return null
  return ((currentTotal - prevTotal) / prevTotal) * 100
})

const topConversionFamily = computed(() => {
  if (!props.membersConversionData?.data) return null
  const data = props.membersConversionData.data.filter(d => d.dimensions.first_activity !== 'none')
  if (!data.length) return null
  const total = data.reduce((s, d) => s + (d.metrics.count ?? 0), 0)
  const top = data.sort((a, b) => (b.metrics.count ?? 0) - (a.metrics.count ?? 0))[0]
  return {
    name: top.dimensions.first_activity,
    count: top.metrics.count ?? 0,
    pct: total > 0 ? Math.round(((top.metrics.count ?? 0) / total) * 100) : 0,
  }
})
</script>
